import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Truck, Zap, Package, Loader2, HelpCircle, ChevronDown } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { FormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  deliveryTiersService,
  type CourierOptimise,
  type DeliveryFulfilmentMode,
  type DeliveryServiceTier,
  type DeliveryServiceTierCode,
  type DeliveryServiceTierScope,
  type DeliveryTierPricingMode,
  type DeliveryTierScopePayload,
  type TierOverrideScope,
} from '@/services/deliveryTiers';
import { dispatchService } from '@/services/dispatch';
import type { DispatchPartner } from '@/types/dispatch';

/**
 * Configure the two customer-facing delivery tiers. The portal shows these
 * (Standard / Express) with a charge; the backend resolves each tier to the
 * optimal real courier at dispatch time using the policy set here. Tiers
 * are off by default — enabling one switches the portal from per-partner
 * options to these tiers.
 */
export function DeliveryTiersPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['delivery-tiers'],
    queryFn: () => deliveryTiersService.list(),
  });
  const partnersQuery = useQuery({
    queryKey: ['dispatch', 'partners'],
    queryFn: () => dispatchService.listPartners(),
    staleTime: 60_000,
  });
  const partners = (partnersQuery.data ?? []).filter((p) => p.status === 'ACTIVE');

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Fulfilment"
        title="Delivery Tiers"
        description="Show customers two simple choices — Standard and Express — each with a price. The backend automatically picks the best courier within each tier at dispatch time. Enable a tier to switch the portal from per-partner options to tiers."
      />

      <HowItWorks />

      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {(data ?? [])
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((tier) => (
              <TierCard key={tier.tier} tier={tier} partners={partners} />
            ))}
        </div>
      )}
    </div>
  );
}

export function HowItWorks() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-border bg-muted/20">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-5 py-3.5 text-left"
      >
        <span className="flex items-center gap-2 text-[13.5px] font-semibold text-foreground">
          <HelpCircle className="h-4 w-4 text-primary" />
          How delivery works — configure it in order
        </span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="space-y-4 border-t border-border/60 px-5 py-4 text-[12.5px] leading-relaxed text-muted-foreground">
          <ol className="space-y-3">
            <Step n={1} title="Connect your carriers — Partners tab">
              Add every courier you ship with (Shiprocket, DHL, …) or a <strong>Manual</strong>{' '}
              partner for in-house delivery. Routing can only assign a <em>connected</em> carrier, so
              do this first.
            </Step>
            <Step n={2} title="Turn on the two plans — Tiers tab">
              Customers see just two options at checkout — <strong>Standard</strong> and{' '}
              <strong>Express</strong> — each with a price (or “Free”); they never see courier names.
              Plans are <strong>off by default</strong> — enable them to switch the portal from
              per-partner options to these two plans.
            </Step>
            <Step n={3} title="Choose how each plan finds a courier — Fulfilment">
              Per plan pick a <strong>Fulfilment</strong> mode: <strong>Automatic</strong> (best
              courier across all your carriers), <strong>Fixed</strong> (always one carrier you
              choose), or <strong>Rule-based</strong> (by destination — step 4). The{' '}
              <strong>selection policy</strong> then picks the actual courier:{' '}
              <em>cheapest</em> or <em>fastest</em>, capped by a max-ETA SLA. Your operator can still
              override per shipment.
            </Step>
            <Step n={4} title="Route by destination — Routing rules tab">
              For <strong>Rule-based</strong> plans, add rules that map a{' '}
              <strong>Destination</strong> (<em>Domestic</em> = your home country ·{' '}
              <em>International</em> = abroad · <em>All</em>) to a carrier — lower priority wins.
              e.g. <strong>Domestic → Shiprocket, International → DHL</strong>. The “Rules for this
              tier” preview shows what each plan actually does.
            </Step>
            <Step n={5} title="Set the price — Static or Live">
              <strong>Static</strong>: the fixed price you set. <strong>Live</strong>: the real
              courier rate + your margin %, floored at the fixed price (needs the destination
              pincode, which the portal sends). Set a plan to <strong>0</strong>, or give free
              delivery above a cart subtotal, for free shipping.
            </Step>
            <Step n={6} title="What customers get + what happens next">
              At checkout we return Standard/Express with a live <strong>delivery date + charge</strong>{' '}
              computed from your carriers per the plan — and those become a{' '}
              <strong>binding commitment</strong>. After payment we book the committed courier (or
              flag your operator if it can’t be), generate the <strong>label + invoice</strong>, and
              track to delivery. Every quote is traceable in <strong>Quotes &amp; audit</strong>, and
              outcomes feed <strong>Analytics</strong> → routing recommendations that sharpen
              auto-routing over time.
            </Step>
          </ol>
          <p className="rounded-lg bg-card px-3 py-2 text-[11.5px]">
            <strong>In short:</strong> Partners → Plans (Tiers) → Routing rules → live quote &amp;
            commitment → dispatch + label/invoice → analytics. Set up Partners first, then your
            Plans and (if rule-based) Routing rules.
          </p>
        </div>
      )}
    </div>
  );
}

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-3">
      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/15 text-[10px] font-bold text-primary">
        {n}
      </span>
      <div className="min-w-0">
        <div className="font-medium text-foreground">{title}</div>
        <div>{children}</div>
      </div>
    </li>
  );
}

const TIER_META: Record<DeliveryServiceTierCode, { icon: typeof Truck; blurb: string }> = {
  STANDARD: { icon: Package, blurb: 'Economy — cheapest courier within the SLA.' },
  EXPRESS: { icon: Zap, blurb: 'Fast — quickest courier within the SLA.' },
};

// ── Per-destination override editor ──────────────────────────────────────
// '' = inherit the base tier for that field.
interface ScopeOverrideState {
  fulfilmentMode: '' | DeliveryFulfilmentMode;
  fixedPartnerId: string;
  optimise: '' | CourierOptimise;
  maxEtaDays: string;
  inr: string;
  usd: string;
  etaDays: string;
}

const emptyScope = (): ScopeOverrideState => ({
  fulfilmentMode: '',
  fixedPartnerId: '',
  optimise: '',
  maxEtaDays: '',
  inr: '',
  usd: '',
  etaDays: '',
});

function scopeFromOverride(o: DeliveryServiceTierScope | undefined): ScopeOverrideState {
  if (!o) return emptyScope();
  return {
    fulfilmentMode: o.fulfilmentMode ?? '',
    fixedPartnerId: o.fixedPartnerId ?? '',
    optimise: o.optimise ?? '',
    maxEtaDays: o.maxEtaDays?.toString() ?? '',
    inr: o.rates?.INR?.toString() ?? '',
    usd: o.rates?.USD?.toString() ?? '',
    etaDays: o.etaDays?.toString() ?? '',
  };
}

/** Build the API payload for one scope, or null when nothing is overridden. */
function scopeToPayload(
  scope: TierOverrideScope,
  s: ScopeOverrideState,
): DeliveryTierScopePayload | null {
  const rates: Record<string, number> = {};
  if (s.inr.trim() !== '') rates.INR = Number(s.inr);
  if (s.usd.trim() !== '') rates.USD = Number(s.usd);
  const hasAny =
    s.fulfilmentMode !== '' ||
    s.optimise !== '' ||
    s.maxEtaDays.trim() !== '' ||
    s.etaDays.trim() !== '' ||
    Object.keys(rates).length > 0;
  if (!hasAny) return null;

  const payload: DeliveryTierScopePayload = { scope };
  if (s.fulfilmentMode !== '') {
    payload.fulfilmentMode = s.fulfilmentMode;
    if (s.fulfilmentMode === 'FIXED_PARTNER') payload.fixedPartnerId = s.fixedPartnerId;
  }
  if (s.optimise !== '') payload.optimise = s.optimise;
  if (s.maxEtaDays.trim() !== '') payload.maxEtaDays = Number(s.maxEtaDays);
  if (Object.keys(rates).length > 0) payload.rates = rates;
  if (s.etaDays.trim() !== '') payload.etaDays = Number(s.etaDays);
  return payload;
}

function ScopeOverrideEditor({
  scope,
  tierCode,
  partners,
  value,
  onChange,
}: {
  scope: TierOverrideScope;
  tierCode: DeliveryServiceTierCode;
  partners: DispatchPartner[];
  value: ScopeOverrideState;
  onChange: (next: ScopeOverrideState) => void;
}) {
  const set = (patch: Partial<ScopeOverrideState>) => onChange({ ...value, ...patch });
  const idp = `${tierCode}-${scope}`;
  const title = scope === 'DOMESTIC' ? 'Domestic' : 'International';
  return (
    <div className="rounded-lg border border-border/60 bg-card p-3">
      <p className="mb-2.5 text-[12px] font-semibold text-foreground">
        {title}
        <span className="ml-2 font-normal text-[11px] text-muted-foreground">
          blank = use default above
        </span>
      </p>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Fulfilment" htmlFor={`${idp}-mode`}>
          <Select
            value={value.fulfilmentMode === '' ? 'INHERIT' : value.fulfilmentMode}
            onValueChange={(v) =>
              set({ fulfilmentMode: v === 'INHERIT' ? '' : (v as DeliveryFulfilmentMode) })
            }
          >
            <SelectTrigger id={`${idp}-mode`} className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="INHERIT">Inherit (default)</SelectItem>
              <SelectItem value="AUTO_OPTIMIZE">Auto-optimize</SelectItem>
              <SelectItem value="FIXED_PARTNER">Fixed carrier</SelectItem>
              <SelectItem value="RULE_BASED">Rule-based</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Optimise for" htmlFor={`${idp}-opt`}>
          <Select
            value={value.optimise === '' ? 'INHERIT' : value.optimise}
            onValueChange={(v) =>
              set({ optimise: v === 'INHERIT' ? '' : (v as CourierOptimise) })
            }
          >
            <SelectTrigger id={`${idp}-opt`} className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="INHERIT">Inherit (default)</SelectItem>
              <SelectItem value="CHEAPEST">Cheapest</SelectItem>
              <SelectItem value="FASTEST">Fastest</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
      </div>

      {value.fulfilmentMode === 'FIXED_PARTNER' && (
        <div className="mt-3">
          <FormField label="Delivery partner" htmlFor={`${idp}-partner`} required>
            <Select
              value={value.fixedPartnerId}
              onValueChange={(v) => set({ fixedPartnerId: v })}
            >
              <SelectTrigger id={`${idp}-partner`} className="h-9">
                <SelectValue placeholder="Select a partner" />
              </SelectTrigger>
              <SelectContent>
                {partners.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                    <span className="ml-2 font-mono text-[10.5px] text-muted-foreground">
                      {p.code}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>
      )}

      <div className="mt-3 grid grid-cols-3 gap-3">
        <FormField label="Price · ₹" htmlFor={`${idp}-inr`}>
          <Input
            id={`${idp}-inr`}
            type="number"
            min={0}
            step="0.01"
            value={value.inr}
            onChange={(e) => set({ inr: e.target.value })}
            placeholder="default"
            className="h-9 text-right tabular-nums"
          />
        </FormField>
        <FormField label="Price · $" htmlFor={`${idp}-usd`}>
          <Input
            id={`${idp}-usd`}
            type="number"
            min={0}
            step="0.01"
            value={value.usd}
            onChange={(e) => set({ usd: e.target.value })}
            placeholder="default"
            className="h-9 text-right tabular-nums"
          />
        </FormField>
        <FormField label="ETA (days)" htmlFor={`${idp}-eta`}>
          <Input
            id={`${idp}-eta`}
            type="number"
            min={0}
            value={value.etaDays}
            onChange={(e) => set({ etaDays: e.target.value })}
            placeholder="default"
            className="h-9 tabular-nums"
          />
        </FormField>
      </div>
      <div className="mt-3">
        <FormField label="Max delivery days" htmlFor={`${idp}-max`} hint="SLA cap — blank inherits">
          <Input
            id={`${idp}-max`}
            type="number"
            min={0}
            value={value.maxEtaDays}
            onChange={(e) => set({ maxEtaDays: e.target.value })}
            placeholder="default"
            className="h-9 w-32 tabular-nums"
          />
        </FormField>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Price + ETA here are the {title.toLowerCase()} charge/delivery-date shown at checkout when
        the carrier has no live rate (e.g. a fixed DHL export) — so they don't fall back to the
        domestic figures.
      </p>
    </div>
  );
}

export function TierCard({
  tier,
  partners,
}: {
  tier: DeliveryServiceTier;
  partners: DispatchPartner[];
}) {
  const qc = useQueryClient();
  const meta = TIER_META[tier.tier];
  const Icon = meta.icon;

  const [enabled, setEnabled] = useState(tier.enabled);
  const [label, setLabel] = useState(tier.label);
  const [etaDays, setEtaDays] = useState(tier.etaDays?.toString() ?? '');
  const [inr, setInr] = useState(tier.rates.INR?.toString() ?? '');
  const [usd, setUsd] = useState(tier.rates.USD?.toString() ?? '');
  const [pricingMode, setPricingMode] = useState<DeliveryTierPricingMode>(tier.pricingMode);
  const [marginPct, setMarginPct] = useState(tier.marginPct?.toString() ?? '0');
  const [roundTo, setRoundTo] = useState(tier.roundTo?.toString() ?? '0');
  const [freeAbove, setFreeAbove] = useState(tier.freeAboveSubtotal?.toString() ?? '');
  const [fulfilmentMode, setFulfilmentMode] = useState<DeliveryFulfilmentMode>(
    tier.fulfilmentMode,
  );
  const [fixedPartnerId, setFixedPartnerId] = useState(tier.fixedPartnerId ?? '');
  const [optimise, setOptimise] = useState<CourierOptimise>(tier.optimise);
  const [maxEtaDays, setMaxEtaDays] = useState(tier.maxEtaDays?.toString() ?? '');
  const [preferAir, setPreferAir] = useState(tier.preferAir);
  const [preferRecommended, setPreferRecommended] = useState(tier.preferRecommended);
  // Per-destination overrides.
  const [splitByDestination, setSplitByDestination] = useState(
    (tier.scopeOverrides?.length ?? 0) > 0,
  );
  const [domestic, setDomestic] = useState<ScopeOverrideState>(
    scopeFromOverride(tier.scopeOverrides?.find((o) => o.scope === 'DOMESTIC')),
  );
  const [international, setInternational] = useState<ScopeOverrideState>(
    scopeFromOverride(tier.scopeOverrides?.find((o) => o.scope === 'INTERNATIONAL')),
  );

  // Re-seed when the upstream row changes (e.g. after a save refetch).
  useEffect(() => {
    setEnabled(tier.enabled);
    setLabel(tier.label);
    setEtaDays(tier.etaDays?.toString() ?? '');
    setInr(tier.rates.INR?.toString() ?? '');
    setUsd(tier.rates.USD?.toString() ?? '');
    setPricingMode(tier.pricingMode);
    setMarginPct(tier.marginPct?.toString() ?? '0');
    setRoundTo(tier.roundTo?.toString() ?? '0');
    setFreeAbove(tier.freeAboveSubtotal?.toString() ?? '');
    setFulfilmentMode(tier.fulfilmentMode);
    setFixedPartnerId(tier.fixedPartnerId ?? '');
    setOptimise(tier.optimise);
    setMaxEtaDays(tier.maxEtaDays?.toString() ?? '');
    setPreferAir(tier.preferAir);
    setPreferRecommended(tier.preferRecommended);
    setSplitByDestination((tier.scopeOverrides?.length ?? 0) > 0);
    setDomestic(scopeFromOverride(tier.scopeOverrides?.find((o) => o.scope === 'DOMESTIC')));
    setInternational(
      scopeFromOverride(tier.scopeOverrides?.find((o) => o.scope === 'INTERNATIONAL')),
    );
  }, [tier]);

  const isDynamic = pricingMode === 'DYNAMIC';

  const save = useMutation({
    mutationFn: () => {
      const rates: Record<string, number> = {};
      if (inr.trim() !== '') rates.INR = Number(inr);
      if (usd.trim() !== '') rates.USD = Number(usd);
      if (fulfilmentMode === 'FIXED_PARTNER' && !fixedPartnerId) {
        throw new Error('Pick a delivery partner for fixed-partner mode.');
      }
      // Per-destination overrides: [] clears them; otherwise the non-empty
      // scopes are sent. Validate any fixed-partner override has a partner.
      let scopes: DeliveryTierScopePayload[] | undefined;
      if (splitByDestination) {
        const built = [
          scopeToPayload('DOMESTIC', domestic),
          scopeToPayload('INTERNATIONAL', international),
        ].filter((s): s is DeliveryTierScopePayload => s !== null);
        for (const s of built) {
          if (s.fulfilmentMode === 'FIXED_PARTNER' && !s.fixedPartnerId) {
            throw new Error(
              `Pick a delivery partner for the ${s.scope.toLowerCase()} fixed-carrier override.`,
            );
          }
        }
        scopes = built;
      } else {
        scopes = [];
      }
      return deliveryTiersService.upsert(tier.tier, {
        enabled,
        label: label.trim() || tier.tier,
        etaDays: etaDays.trim() !== '' ? Number(etaDays) : undefined,
        rates,
        pricingMode,
        marginPct: marginPct.trim() !== '' ? Number(marginPct) : 0,
        roundTo: roundTo.trim() !== '' ? Number(roundTo) : 0,
        freeAboveSubtotal: freeAbove.trim() !== '' ? Number(freeAbove) : null,
        fulfilmentMode,
        fixedPartnerId:
          fulfilmentMode === 'FIXED_PARTNER' ? fixedPartnerId : undefined,
        optimise,
        maxEtaDays: maxEtaDays.trim() !== '' ? Number(maxEtaDays) : undefined,
        preferAir,
        preferRecommended,
        scopes,
      });
    },
    onSuccess: () => {
      toast.success(`${tier.tier === 'STANDARD' ? 'Standard' : 'Express'} tier saved`);
      qc.invalidateQueries({ queryKey: ['delivery-tiers'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not save tier'),
  });

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary ring-1 ring-inset ring-primary/15">
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <h3 className="text-[15px] font-semibold text-foreground">
              {tier.tier === 'STANDARD' ? 'Standard' : 'Express'}
            </h3>
            <p className="text-[11.5px] text-muted-foreground">{meta.blurb}</p>
          </div>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-[12px] font-medium">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          {enabled ? 'Enabled' : 'Disabled'}
        </label>
      </div>

      <div className="mt-5 space-y-4">
        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-7">
            <FormField label="Customer label" htmlFor={`${tier.tier}-label`}>
              <Input
                id={`${tier.tier}-label`}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Standard (3–5 days)"
                className="h-9"
              />
            </FormField>
          </div>
          <div className="col-span-5">
            <FormField label="Shown ETA (days)" htmlFor={`${tier.tier}-eta`}>
              <Input
                id={`${tier.tier}-eta`}
                type="number"
                min={0}
                value={etaDays}
                onChange={(e) => setEtaDays(e.target.value)}
                className="h-9 tabular-nums"
              />
            </FormField>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Price · INR (₹)" htmlFor={`${tier.tier}-inr`}>
            <Input
              id={`${tier.tier}-inr`}
              type="number"
              min={0}
              step="0.01"
              value={inr}
              onChange={(e) => setInr(e.target.value)}
              placeholder="0"
              className="h-9 text-right tabular-nums"
            />
          </FormField>
          <FormField label="Price · USD ($)" htmlFor={`${tier.tier}-usd`}>
            <Input
              id={`${tier.tier}-usd`}
              type="number"
              min={0}
              step="0.01"
              value={usd}
              onChange={(e) => setUsd(e.target.value)}
              placeholder="0"
              className="h-9 text-right tabular-nums"
            />
          </FormField>
        </div>

        <div className="rounded-xl border border-border/60 bg-muted/20 p-3.5">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Pricing
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setPricingMode('STATIC')}
              className={`rounded-md border px-3 py-2 text-left text-[12px] ${
                !isDynamic
                  ? 'border-primary/30 ring-1 ring-inset ring-primary/15'
                  : 'border-border/70'
              }`}
            >
              <div className="font-medium">Static price</div>
              <div className="text-[11px] text-muted-foreground">Charge the fixed price above</div>
            </button>
            <button
              type="button"
              onClick={() => setPricingMode('DYNAMIC')}
              className={`rounded-md border px-3 py-2 text-left text-[12px] ${
                isDynamic
                  ? 'border-primary/30 ring-1 ring-inset ring-primary/15'
                  : 'border-border/70'
              }`}
            >
              <div className="font-medium">Live (dynamic)</div>
              <div className="text-[11px] text-muted-foreground">Price from the real courier rate</div>
            </button>
          </div>

          {isDynamic && (
            <>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <FormField label="Margin %" htmlFor={`${tier.tier}-margin`} hint="On the live rate">
                  <Input
                    id={`${tier.tier}-margin`}
                    type="number"
                    min={0}
                    step="0.5"
                    value={marginPct}
                    onChange={(e) => setMarginPct(e.target.value)}
                    className="h-9 text-right tabular-nums"
                  />
                </FormField>
                <FormField label="Round up to (₹)" htmlFor={`${tier.tier}-round`} hint="0 = off">
                  <Input
                    id={`${tier.tier}-round`}
                    type="number"
                    min={0}
                    value={roundTo}
                    onChange={(e) => setRoundTo(e.target.value)}
                    placeholder="0"
                    className="h-9 text-right tabular-nums"
                  />
                </FormField>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                The INR price above is used as the <strong>floor</strong> and the
                fallback when a live rate can't be fetched. Live pricing applies to
                INR checkout; other currencies use the fixed price.
              </p>
            </>
          )}

          <div className="mt-3">
            <FormField
              label="Free delivery above subtotal (₹)"
              htmlFor={`${tier.tier}-free`}
              hint="Optional — leave blank to never offer free"
            >
              <Input
                id={`${tier.tier}-free`}
                type="number"
                min={0}
                step="0.01"
                value={freeAbove}
                onChange={(e) => setFreeAbove(e.target.value)}
                placeholder="none"
                className="h-9 text-right tabular-nums"
              />
            </FormField>
          </div>
        </div>

        <div className="rounded-xl border border-border/60 bg-muted/20 p-3.5">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Fulfilment — which carrier ships this tier
          </p>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setFulfilmentMode('AUTO_OPTIMIZE')}
              className={`rounded-md border px-3 py-2 text-left text-[12px] ${
                fulfilmentMode === 'AUTO_OPTIMIZE'
                  ? 'border-primary/30 ring-1 ring-inset ring-primary/15'
                  : 'border-border/70'
              }`}
            >
              <div className="font-medium">Auto-optimize</div>
              <div className="text-[11px] text-muted-foreground">Best across all carriers</div>
            </button>
            <button
              type="button"
              onClick={() => setFulfilmentMode('FIXED_PARTNER')}
              className={`rounded-md border px-3 py-2 text-left text-[12px] ${
                fulfilmentMode === 'FIXED_PARTNER'
                  ? 'border-primary/30 ring-1 ring-inset ring-primary/15'
                  : 'border-border/70'
              }`}
            >
              <div className="font-medium">Fixed carrier</div>
              <div className="text-[11px] text-muted-foreground">One carrier you choose</div>
            </button>
            <button
              type="button"
              onClick={() => setFulfilmentMode('RULE_BASED')}
              className={`rounded-md border px-3 py-2 text-left text-[12px] ${
                fulfilmentMode === 'RULE_BASED'
                  ? 'border-primary/30 ring-1 ring-inset ring-primary/15'
                  : 'border-border/70'
              }`}
            >
              <div className="font-medium">Rule-based</div>
              <div className="text-[11px] text-muted-foreground">Routing rules decide</div>
            </button>
          </div>
          {fulfilmentMode === 'RULE_BASED' && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              The carrier comes from your routing rules (scope a rule to this tier). Manage them
              on the Dispatcher.
            </p>
          )}
          {fulfilmentMode === 'FIXED_PARTNER' && (
            <div className="mt-3">
              <FormField label="Delivery partner" htmlFor={`${tier.tier}-partner`} required>
                <Select value={fixedPartnerId} onValueChange={setFixedPartnerId}>
                  <SelectTrigger id={`${tier.tier}-partner`} className="h-9">
                    <SelectValue placeholder="Select a partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {partners.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                        <span className="ml-2 font-mono text-[10.5px] text-muted-foreground">
                          {p.code}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                Every {tier.tier === 'STANDARD' ? 'Standard' : 'Express'} shipment books via this
                carrier. The policy below still picks the best courier <em>within</em> it.
              </p>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border/60 bg-muted/20 p-3.5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Domestic vs International
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Ship this tier differently by destination. The settings above are the default;
                blank override fields inherit them.
              </p>
            </div>
            <label className="flex shrink-0 cursor-pointer items-center gap-2 text-[12px] font-medium">
              <input
                type="checkbox"
                checked={splitByDestination}
                onChange={(e) => setSplitByDestination(e.target.checked)}
              />
              {splitByDestination ? 'On' : 'Off'}
            </label>
          </div>
          {splitByDestination && (
            <div className="mt-3 space-y-3">
              <ScopeOverrideEditor
                scope="DOMESTIC"
                tierCode={tier.tier}
                partners={partners}
                value={domestic}
                onChange={setDomestic}
              />
              <ScopeOverrideEditor
                scope="INTERNATIONAL"
                tierCode={tier.tier}
                partners={partners}
                value={international}
                onChange={setInternational}
              />
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border/60 bg-muted/20 p-3.5">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Courier selection policy
          </p>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Optimise for" htmlFor={`${tier.tier}-opt`}>
              <Select value={optimise} onValueChange={(v) => setOptimise(v as CourierOptimise)}>
                <SelectTrigger id={`${tier.tier}-opt`} className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CHEAPEST">Cheapest</SelectItem>
                  <SelectItem value="FASTEST">Fastest</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Max delivery days" htmlFor={`${tier.tier}-max`} hint="SLA cap">
              <Input
                id={`${tier.tier}-max`}
                type="number"
                min={0}
                value={maxEtaDays}
                onChange={(e) => setMaxEtaDays(e.target.value)}
                placeholder="none"
                className="h-9 tabular-nums"
              />
            </FormField>
          </div>
          <div className="mt-3 flex flex-wrap gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-[12px]">
              <input
                type="checkbox"
                checked={preferAir}
                onChange={(e) => setPreferAir(e.target.checked)}
              />
              Prefer air over surface
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-[12px]">
              <input
                type="checkbox"
                checked={preferRecommended}
                onChange={(e) => setPreferRecommended(e.target.checked)}
              />
              Prefer recommended courier
            </label>
          </div>
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Truck className="h-3.5 w-3.5" />}
          Save tier
        </Button>
      </div>
    </div>
  );
}
